import { Logger } from '@nestjs/common';
import { partition } from 'ramda';
import { BaseError } from 'src/types/base-error';
import {
  LeftResult,
  ResolvedRightResult,
  Result,
  RightResult,
} from 'src/types/result';
import { delay } from 'src/utils/delay';
import { retryForWithBackoff } from 'src/utils/retry';

export const getResult = <T, E extends BaseError | BaseError[] = any>(
  value: T,
): Result<E, T> => {
  return {
    success: true as const,
    value,

    asRightOf: <ENew extends BaseError | BaseError[]>() =>
      getResult<T, ENew>(value),

    asErrorResult: (error) => getErrorResult(error),

    flatMap: (mapFn) => mapFn(value),
    flatMapAsync: async (mapFn) => await mapFn(value),

    map: (mapFn) => getResult(mapFn(value)),
    mapAsync: async (mapFn) => getResult(await mapFn(value)),

    mapLeft: () => getResult(value),
    mapLeftAsync: async () => getResult(value),

    log: (logger, messageFn) => {
      logger.log(messageFn(value));

      return getResult(value);
    },

    logDebug: (logger, messageFn) => {
      logger.debug(messageFn(value));

      return getResult(value);
    },

    logIfError: () => getResult(value),
  };
};

export const getErrorResult = <E extends BaseError | BaseError[], T = any>(
  error: E,
): Result<E, T> => {
  return {
    success: false as const,
    error,

    asLeftOf: <TNew>() => getErrorResult<E, TNew>(error),

    asErrorResult: (error) => getErrorResult(error),

    flatMap: () => getErrorResult(error),
    flatMapAsync: async () => getErrorResult(error),

    map: () => getErrorResult(error),
    mapAsync: async () => getErrorResult(error),

    mapLeft: (mapFn) => getErrorResult(mapFn(error)),
    mapLeftAsync: async (mapFn) => getErrorResult(mapFn(error)),

    log: () => getErrorResult(error),
    logDebug: () => getErrorResult(error),

    logIfError: (
      logger: Logger,
      additionalContext?: Parameters<JSON['stringify']>[0],
    ) => {
      logErrorIfLeft(logger, getErrorResult(error), additionalContext);

      return getErrorResult(error);
    },
  };
};

export const getResultOr = async <E extends BaseError | BaseError[], T>(
  getError: (error: Error) => E,
  fn: () => Promise<T>,
): Promise<Result<E, T>> => {
  try {
    return getResult(await fn());
  } catch (error) {
    return getErrorResult(getError(error));
  }
};

export const isErrorOfType =
  <E>(errorType: new (...args: any[]) => E) =>
  (e: unknown): e is E =>
    e instanceof errorType;

export const getRetryResult = async <E extends BaseError | BaseError[], T>(
  {
    attempts,
    initialIntervalMs = 0,
    backoffMultiplier = 1,
    onFailedAttempt,
  }: {
    attempts: number;
    initialIntervalMs?: number;
    backoffMultiplier?: number;
    onFailedAttempt?: (
      error: E,
      context: {
        attemptNumber: number;
        attemptsRemaining: number;
        currentIntervalMs: number;
      },
    ) => void;
  },
  fn: () => Promise<Result<E, T>>,
): Promise<Result<E, T>> => {
  let result: Result<E, T>;

  let currentIntervalMs = initialIntervalMs;

  for (let i = 1; i <= attempts; ++i) {
    result = await fn();

    if (result.success) {
      return result;
    }

    const attemptsRemaining = attempts - i;

    if (onFailedAttempt) {
      onFailedAttempt(result.error, {
        attemptNumber: i,
        attemptsRemaining,
        currentIntervalMs,
      });
    }

    if (attemptsRemaining > 0) {
      await delay(currentIntervalMs);

      currentIntervalMs = Math.floor(currentIntervalMs * backoffMultiplier);
    }
  }

  return result!;
};

type TTraversedResultLeft<
  E extends BaseError | BaseError[],
  TResults extends readonly Result<E, any>[] | Result<E, any>[],
> = Parameters<Parameters<TResults[number]['mapLeft']>[0]>[0];

type TTraversedResultRight<TResults extends readonly Result<any, any>[]> =
  TResults extends readonly [
    infer U extends Result<any, any>,
    ...infer R extends readonly Result<any, any>[],
  ]
    ? [U extends Result<any, infer T> ? T : never, ...TTraversedResultRight<R>]
    : TResults extends readonly []
      ? []
      : TResults extends Result<any, infer T>[]
        ? T[]
        : TResults;

export const traverseResults = <
  E extends BaseError,
  TValue extends any,
  TResults extends readonly Result<E, TValue>[] | Result<E, TValue>[],
>(
  results: TResults,
): Result<
  TTraversedResultLeft<E, TResults>,
  TTraversedResultRight<TResults>
> => {
  const firstErrorResult = results.find((it) => !it.success) as
    LeftResult<E> | undefined;
  if (firstErrorResult) {
    return getErrorResult(firstErrorResult.error);
  }

  return getResult(
    (results as RightResult<TValue>[]).map(
      (it) => it.value,
    ) as TTraversedResultRight<TResults>,
  );
};

export const traverseAllResults = <
  E extends BaseError,
  TValue extends any,
  TResults extends readonly Result<E, TValue>[] | Result<E, TValue>[],
>(
  results: TResults,
): Result<
  TTraversedResultLeft<E, TResults>[],
  TTraversedResultRight<TResults>
> => {
  const errorResults = results.filter((it) => !it.success) as LeftResult<E>[];
  if (errorResults.length > 0) {
    return getErrorResult(errorResults.map((it) => it.error));
  }

  return getResult(
    (results as RightResult<TValue>[]).map(
      (it) => it.value,
    ) as TTraversedResultRight<TResults>,
  );
};

export const partitionResults = <E extends BaseError | BaseError[], T>(
  results: Result<E, T>[],
): [LeftResult<E>[], RightResult<T>[]] =>
  partition((it) => !it.success, results);

export const isResult = (it: unknown): it is Result<any, any> =>
  typeof it === 'object' &&
  !!it &&
  it.hasOwnProperty('success') &&
  (it.hasOwnProperty('error') || it.hasOwnProperty('value'));

export const handleResult = <E extends BaseError | BaseError[], T>(
  result: Result<E, T>,
  ifSuccess: (value: T) => void,
  ifError: (error: E) => void,
): void => {
  if (result.success) {
    ifSuccess(result.value);
  } else {
    ifError(result.error);
  }
};

// todo - remove - this API is a bit ambiguous with return value
export const getValueOrHandle = <
  E extends BaseError | BaseError[],
  TResult extends Result<E, any>,
>(
  result: TResult,
  orHandleFn: (error: E) => void,
): TResult extends ResolvedRightResult<any, infer T> ? T : never => {
  if (result.success) {
    return result.value;
  }

  return orHandleFn((result as LeftResult<E>).error) as any;
};

const getCompoundMessage = (it: Error): string =>
  `${it.message}${it.cause ? `: ${getCompoundMessage(it.cause as Error)}` : ''}`;

const logError = (logger: Logger, error: BaseError | BaseError[]) => {
  [error].flat().forEach((it) => {
    logger.error(
      getCompoundMessage(it),
      it.cause || it.context
        ? JSON.stringify({
            cause: it.cause
              ? {
                  name: (it.cause as Error).name,
                  message: (it.cause as Error).message,
                }
              : undefined,
            context: it.context,
          })
        : undefined,
    );
  });
};

export const getValueOrLogError = <
  TResult extends Result<BaseError | BaseError[], any>,
>(
  logger: Logger,
  result: TResult,
) => getValueOrHandle(result, (error) => logError(logger, error));

export const logErrorIfLeft = <
  TResult extends Result<BaseError | BaseError[], any>,
>(
  logger: Logger,
  result: TResult,
  additionalContext?: Parameters<JSON['stringify']>[0],
) => {
  if (!result.success) {
    logError(logger, result.error);

    if (additionalContext) {
      logger.error('additional context', JSON.stringify(additionalContext));
    }
  }
};

export const getRetryResultWithBackoff = async <E extends BaseError, T>(
  {
    attempts,
    initialDelayMs = 1000,
    backoffMultiplier = 2,
    handleError,
  }: {
    attempts: number;
    initialDelayMs?: number;
    backoffMultiplier?: number;
    handleError?: (
      error: E,
      context: {
        attemptNum: number;
        attemptsRemaining: number;
        delayMs: number;
      },
    ) => void;
  },
  fn: () => Promise<Result<E, T>>,
): Promise<Result<E, T>> => {
  return retryForWithBackoff(
    {
      attempts,
      initialDelayMs,
      backoffMultiplier,
      shouldContinue: (result) => result.success,
      onError: handleError
        ? (result, context) => {
            if (!result.success) {
              handleError(result.error, context);
            }
          }
        : undefined,
    },
    fn,
  );
};

export const getRetryResultWithBackoffOr = async <E extends BaseError, T>(
  getError: (error: Error) => E,
  {
    attempts,
    initialDelayMs = 1000,
    backoffMultiplier = 2,
    handleError,
  }: {
    attempts: number;
    initialDelayMs?: number;
    backoffMultiplier?: number;
    handleError?: (
      error: E,
      context: {
        attemptNum: number;
        attemptsRemaining: number;
        delayMs: number;
      },
    ) => void;
  },
  fn: () => Promise<T>,
): Promise<Result<E, T>> => {
  return getRetryResultWithBackoff(
    {
      attempts,
      initialDelayMs,
      backoffMultiplier,
      handleError,
    },
    async () => {
      try {
        const result = await fn();
        return getResult<T, E>(result);
      } catch (error) {
        return getErrorResult<E, T>(getError(error as Error));
      }
    },
  );
};
